import { create } from 'zustand'

import { analyticsApi } from '@/api/analytics.api'
import type { RateItem } from '@/types/analytics'

interface RatesStore {
  // Cache of rates by currency and date (key: currency_date)
  rates: Record<string, RateItem>
  // Loading states per key
  loading: Record<string, boolean>
  // Global error or per request? Let's stick to simple
  error: string | null

  // Actions
  fetchRate: (currency: string, date: string) => Promise<RateItem | undefined>
  fetchLatestRates: () => Promise<void>
  clearCache: () => void
}

export const useRatesStore = create<RatesStore>((set, get) => ({
  rates: {},
  loading: {},
  error: null,

  fetchLatestRates: async () => {
    set(state => ({ loading: { ...state.loading, latest: true }, error: null }))
    try {
      const response = await analyticsApi.getRates()
      const newRates: Record<string, RateItem> = {}

      response.Rates.forEach(rate => {
        // Cache as latest (without date in key) and also with its specific date
        const latestKey = `${rate.Currency.toLowerCase()}_latest`
        const dateKey = `${rate.Currency.toLowerCase()}_${rate.Date}`
        newRates[latestKey] = rate
        if (rate.Date) {
          newRates[dateKey] = rate
        }
      })

      set(state => ({
        rates: { ...state.rates, ...newRates },
        loading: { ...state.loading, latest: false },
      }))
    } catch (err: any) {
      set(state => ({
        error: err.message,
        loading: { ...state.loading, latest: false },
      }))
    }
  },

  fetchRate: async (currency, date) => {
    const upperCurrency = currency.toUpperCase()
    if (upperCurrency === 'PLN') {
      const plnRate: RateItem = {
        Currency: 'pln' as any,
        Rate: 1,
        Date: date,
      }
      return plnRate
    }

    const key = `${currency.toLowerCase()}_${date}`

    // Return from cache if exists
    if (get().rates[key]) {
      return get().rates[key]
    }

    set(state => ({
      loading: { ...state.loading, [key]: true },
      error: null,
    }))

    try {
      const response = await analyticsApi.getRates({
        date,
        currency: currency.toLowerCase(),
      })

      const rate = response.Rates[0]

      if (!rate) {
        throw new Error('Rate not found')
      }

      set(state => ({
        rates: { ...state.rates, [key]: rate },
        loading: { ...state.loading, [key]: false },
      }))

      return rate
    } catch (err: any) {
      const message = err.message || 'Failed to fetch rate'
      set(state => ({
        error: message,
        loading: { ...state.loading, [key]: false },
      }))
      throw err
    }
  },

  clearCache: () => set({ rates: {}, loading: {}, error: null }),
}))
